/**
 * Message ids, ranges and the message catalogue (PROTOCOL.md sections 2 and 3), as data the validator and
 * both endpoints read.
 */

/**
 * Message ids.
 */
export const MessageType = {
  Hello: 0x0001,
  Welcome: 0x0002,
  Ping: 0x0003,
  Pong: 0x0004,
  Error: 0x0005,
  Maintenance: 0x0006,
  PerfOffer: 0x0007,
  PerfRequest: 0x0008,
  PerfReport: 0x0009,
  FeedbackOffer: 0x000a,
  ModerationOffer: 0x000b,
  Moderation: 0x000c,
  StaffAlert: 0x000d,
  FeedbackAccess: 0x000e,
  Queue: 0x000f,
  HandoffInitiated: 0x0010,
  HandoffResume: 0x0011,
  SpawnConfirmed: 0x0012,
  QueueLeave: 0x0013,
  Leave: 0x0014,
  LeaveAnswer: 0x0015,
  StaffBadgeOffer: 0x0016,
  StaffNotice: 0x0017,
  StaffNoticeAck: 0x0018,
  StaffNoticeSeen: 0x0019,
  Input: 0x0100,
  Target: 0x0200,
  Combat: 0x0201,
  Vitals: 0x0202,
  Effects: 0x0203,
  TargetEffects: 0x0204,
  Progress: 0x0205,
  RespawnOffer: 0x0206,
  Respawn: 0x0207,
  Projectiles: 0x0208,
  Attributes: 0x0209,
  SpendAttributes: 0x020a,
  RespecAttributes: 0x020b,
  AttributeRefused: 0x020c,
  Act: 0x0300,
  PvpFlagToggle: 0x0400,
  PvpState: 0x0401,
  DuelChallenge: 0x0402,
  DuelResponse: 0x0403,
  DuelStarted: 0x0404,
  DuelEnded: 0x0405,
  PvpSwitch: 0x0406,
  Say: 0x0500,
  Whisper: 0x0501,
  Block: 0x0502,
  Unblock: 0x0503,
  Said: 0x0504,
  Whispered: 0x0505,
  Blocks: 0x0506,
  ChatRefused: 0x0507,
  Pose: 0x0508,
  Posed: 0x0509,
  NameLookup: 0x050a,
  NameMatches: 0x050b,
  ChannelSay: 0x050c,
  ChannelJoin: 0x050d,
  ChannelLeave: 0x050e,
  ChannelMute: 0x050f,
  ChannelSaid: 0x0510,
  Channels: 0x0511,
  ChannelMuted: 0x0512,
  Afk: 0x0513,
  Away: 0x0514,
  WhisperAway: 0x0515,
  PartyInvite: 0x0516,
  PartyAnswer: 0x0517,
  PartyLeave: 0x0518,
  PartyRemove: 0x0519,
  PartyPromote: 0x051a,
  PartySay: 0x051b,
  PartyInvited: 0x051c,
  Party: 0x051d,
  PartyNotice: 0x051e,
  PartySaid: 0x051f,
  PartyRefused: 0x0520,
  PartyGrant: 0x0521,
  Emote: 0x0522,
  Emotes: 0x0523,
  EmojiShown: 0x0524,
  ActionShown: 0x0525,
  SpeakerBadge: 0x0526,
  PartyInviteLookup: 0x0527,
  PartyInviteMatches: 0x0528,
  Bag: 0x0600,
  BagChanged: 0x0601,
  UseItem: 0x0602,
  BagMove: 0x0603,
  ItemRefused: 0x0604,
  Loot: 0x0605,
  LootTake: 0x0606,
  LootTaken: 0x0607,
  Bar: 0x0608,
  BarSet: 0x0609,
  BagSplit: 0x060a,
  DestroyItem: 0x060b,
  Wallet: 0x060c,
  BagSort: 0x060d,
  BagKeep: 0x060e,
  BagKept: 0x060f,
  LootGained: 0x0610,
  PickUp: 0x0611,
  PickUpRefused: 0x0612,
  DropItem: 0x0613,
  ToolBelt: 0x0614,
  ToolBeltSet: 0x0615,
  Snapshot: 0x0700,
  Shrines: 0x0701,
  ThingEvent: 0x0702,
  Looks: 0x0705,
  MapPlaces: 0x0706,
  PartyPositions: 0x0707,
  Explored: 0x0708,
  PartyVitals: 0x0709,
  TravelOffer: 0x070a,
  TravelState: 0x070b,
  Harvested: 0x070c,
  HarvestEvent: 0x070d,
  EdgeView: 0x070e,
  EdgeNear: 0x070f,
  Interact: 0x0800,
  Interaction: 0x0801,
  ReadingLayout: 0x0802,
  TravelAsk: 0x0803,
  Travel: 0x0804,
  TravelCancel: 0x0805,
  Dialogue: 0x0806,
  DialogueChoose: 0x0807,
  DialogueClose: 0x0808,
  Harvest: 0x0809,
  HarvestState: 0x080a,
  DoorRefused: 0x080b,
  DoorPrompt: 0x080c,
  CompanionSet: 0x080d,
  CompanionName: 0x080e,
  CompanionSay: 0x080f,
  CompanionNaming: 0x0810,
  CompanionState: 0x0811,
  Sky: 0x0812,
  DoorBusy: 0x0813,
  Source: 0x0814,
} as const;
export type MessageType = (typeof MessageType)[keyof typeof MessageType];

/**
 * Who sends a message.
 */
export type MessageDirection = "clientToServer" | "serverToClient";

/**
 * Who may send a catalogued message: one side, or either (`both`), for a message whose fields mean the same from each.
 */
export type CatalogueDirection = MessageDirection | "both";

/**
 * Which frame carries a message.
 */
export type FrameKind = "text" | "binary";

/**
 * One catalogue entry.
 */
export interface MessageInfo {
  readonly type: MessageType;
  readonly name: string;
  readonly direction: CatalogueDirection;
  readonly frame: FrameKind;
  readonly live: boolean;
}

/**
 * Every numbered message. `live` is the readiness a message has in this build: true for the ones it handles. A client
 * message declared ahead of the row that handles it (the client's two handoff messages, `handoffResume` and
 * `spawnConfirmed`, kept for a direct connection, and the duel requests) is dropped and counted when it arrives, never closed (ADR-074 decision 1). Chat's eight (ADR-068 decision
 * 3) sit in the social range, the player's four requests, then the server's four answers; after them its amendment's
 * four (item 7), a roleplay action each way, then a name lookup and its answer; and after those its amendment "Global,
 * Trade and Help"'s seven (item 6): a line to a channel, a join, a leave and a GM's mute, then a channel's line, the
 * character's channels and mutes, and the answer to a mute; and after those ADR-090's three: `/afk`, the player's own
 * away state, which is also the offer a page waits for before it sends `afk` or the input's Used and Away bits, and
 * the line a whisper to an AFK player answers its sender with; and, past the party's twelve (ADR-101 decision 19, `0x0516` to `0x0521`: the leader's and the member's
 * requests, the prompt, the roster, a notice, a line, a refusal and the grant; declared and not live until the rows that
 * handle and send them, P2.13-T2 to T4), ADR-114's three (decision 6.1): `emote`, an emoji or a roleplay action by its kind and
 * id, `emotes`, the emote sets the account holds, which is also the offer a page waits for before it sends `emote`, and
 * `emojiShown`, an emoji over a character in view. `emote` is live from the version whose gateway handles it
 * (PROTOCOL.md 6.34.0); the two server messages were live from the version that declared them, as `shrines` was, so
 * a page reads them from the first one sent. The combat range ends with the character window's four (ADR-138's amendment of
 * 2026-10-31, 6.99.0, `0x0209` to `0x020C`): `attributes`, `spendAttributes`, `respecAttributes` and `attributeRefused`, all
 * live. The combat, ability and PvP ranges
 * hold protocol 5's ten (ADR-074 decision 1), of which `target` (from P2.2-T3), `combat`, `vitals`, `act`, `pvpFlagToggle` and
 * `pvpState` are handled, ADR-076's `pvpSwitch` after them, and ADR-083's `progress`, a character's own level and XP,
 * after `targetEffects`, and ADR-095's `projectiles` (decision 11, `0x0208`), one frame a tick of the shots a page may
 * see, live since the map server sends it (P2.4-T21). The system range holds ADR-078's `maintenance`, the schedule a
 * joined page counts down to, and ADR-087's performance monitor: the gateway's offer to a GM's page, the page's
 * request and the gateway's report; then the gateway's offer of the staff feedback form (ADR-081's amendment of
 * 2026-10-03 on P1.9-T72); then moderation's three (ADR-056's amendment of 2026-10-04): the offer a GM's page waits
 * for, what a player a GM acted on is told, and the alert the staff hear; then `feedbackAccess` (ADR-081's amendment of 2026-10-05), which tells a page its account's feedback is paused, off or open again. Then the login queue's two (ADR-127 decision 5): `queue` at the free `0x000F`, a waiting connection's place, estimate and the line's length, live from the version that declares it as every server message is, and `queueLeave` at `0x0013`, after the handoff's three, of which `handoffInitiated` is live from 6.91.0, the server telling the page its character moves to another map on the same socket (ADR-106 decision 19), the page's Leave queue, declared in 6.61.0 and live from 6.62.0, when the gateway handles it (P1.4-T163). The inventory range holds ADR-091's eight (decision 20): the bag, a change to it, a use, a
 * move, a refusal of either, a chest's lines, a take and its answer; the three requests are declared and not live until
 * the rows that handle them (P2.8-T7, T14 and T18), so a gateway drops them as not ready. The interaction range holds a
 * verb, its reply, and ADR-077's `readingLayout`, which goes before a reply whose text turns pages; then ADR-137's
 * three travel requests (decision 12, `0x0803` to `0x0805`), declared by P2.11-T36 and live from P2.11-T37, whose
 * map server answers them; then ADR-136's three (decision 5.3, `0x0806` to `0x0808`): `dialogue`, the
 * node of a conversation the server shows, `dialogueChoose`, the page's choice by index, and `dialogueClose`, which
 * either side sends (`both`), all live from the version that declares them; then ADR-142's `harvest` and `harvestState`
 * (`0x0809` and `0x080A`), and ADR-122's `doorRefused` (`0x080B`, 6.92.0), why the server left a character standing in a
 * door, and `doorPrompt` (`0x080C`, 6.93.0), the prompt of a Press door the character stands in, both live from the
 * version that declares them; then ADR-142's `companionSet` (`0x080D`) and `companionName` (`0x080E`), the page's send home
 * and its choice of a name, `companionSet` live from 6.97.0 and `companionName` live
 * from 6.95.0, `companionSay` (`0x080F`), a tip over the companion, live from the version that declares it (6.94.0), and
 * `companionNaming` (`0x0810`, 6.95.0), where the naming at the offer stands, and `companionState` (`0x0811`, 6.97.0), whether the player's companion has joined, is out, and his name, both live from the version that declares them; then ADR-141's `sky` (`0x0812`, 6.100.0), the override the sky is under, live from the version that declares it; then ADR-122's `doorBusy` (`0x0813`, 6.102.0), which says a door waits on the cap on transfers. The world state range holds the snapshot, ADR-147's `edgeView` and `edgeNear` (decisions 12, 13 and 31, `0x070E` and `0x070F`, 6.96.0), a neighbour map's edge band and which neighbour a page now receives, both live from the version that declares them, ADR-137's
 * `travelOffer` and `travelState` (decision 12, `0x070A` and `0x070B`, live from the version that declares them as
 * `mapPlaces` was), ADR-096's two (decision 45): `shrines`, the shrines the player's own character has
 * discovered, and `thingEvent`, events on things in view; and ADR-094's `looks` (decision 6, `0x0705`), the looks of
 * the characters entering a view; and ADR-100's three (decisions 6, 7 and 22, `0x0706` to `0x0708`): `mapPlaces`, the
 * map's Spawn Point and the shrines the character has discovered on it, `partyPositions`, the receiver's party members
 * on its map, and `explored`, the fog cells the character has explored; then ADR-101's `partyVitals` (decision 20, `0x0709`), the vitals of the receiver's party members its map server holds on its map, live since the map server sends it (P2.13-T7) and the page draws it (P2.13-T8). `mapPlaces` and `explored` are live from the
 * version that declares them, as `shrines` was; `partyPositions` is live since the map server sends it (P2.12-T14) and
 * no page reads it yet (P2.12-T15). `thingEvent` is live, sent by the map server (P2.11-T6); `looks` is declared and not
 * live until it is sent (P2.10-T7). `shrines` is live from the version that declares it: the text
 * parser answers malformed for a server message that is not live, so a page built before the map server sends it
 * (P2.11-T5) must already accept it, or the first `shrines` would close its socket.
 */
export const CATALOGUE: readonly MessageInfo[] = [
  { type: MessageType.Hello, name: "hello", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Welcome, name: "welcome", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Ping, name: "ping", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Pong, name: "pong", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Error, name: "error", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Maintenance, name: "maintenance", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PerfOffer, name: "perfOffer", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PerfRequest, name: "perfRequest", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PerfReport, name: "perfReport", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.FeedbackOffer, name: "feedbackOffer", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ModerationOffer, name: "moderationOffer", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Moderation, name: "moderation", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.StaffAlert, name: "staffAlert", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.FeedbackAccess, name: "feedbackAccess", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Queue, name: "queue", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.HandoffInitiated, name: "handoffInitiated", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.HandoffResume, name: "handoffResume", direction: "clientToServer", frame: "text", live: false },
  { type: MessageType.SpawnConfirmed, name: "spawnConfirmed", direction: "serverToClient", frame: "text", live: false },
  { type: MessageType.QueueLeave, name: "queueLeave", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Leave, name: "leave", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.LeaveAnswer, name: "leaveAnswer", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.StaffBadgeOffer, name: "staffBadgeOffer", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.StaffNotice, name: "staffNotice", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.StaffNoticeAck, name: "staffNoticeAck", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.StaffNoticeSeen, name: "staffNoticeSeen", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Input, name: "input", direction: "clientToServer", frame: "binary", live: true },
  { type: MessageType.Target, name: "target", direction: "clientToServer", frame: "binary", live: true },
  { type: MessageType.Combat, name: "combat", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Vitals, name: "vitals", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Effects, name: "effects", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.TargetEffects, name: "targetEffects", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Progress, name: "progress", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.RespawnOffer, name: "respawnOffer", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Respawn, name: "respawn", direction: "clientToServer", frame: "binary", live: true },
  { type: MessageType.Projectiles, name: "projectiles", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Attributes, name: "attributes", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.SpendAttributes, name: "spendAttributes", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.RespecAttributes, name: "respecAttributes", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.AttributeRefused, name: "attributeRefused", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Act, name: "act", direction: "clientToServer", frame: "binary", live: true },
  { type: MessageType.PvpFlagToggle, name: "pvpFlagToggle", direction: "clientToServer", frame: "binary", live: true },
  { type: MessageType.PvpState, name: "pvpState", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.DuelChallenge, name: "duelChallenge", direction: "clientToServer", frame: "text", live: false },
  { type: MessageType.DuelResponse, name: "duelResponse", direction: "clientToServer", frame: "text", live: false },
  { type: MessageType.DuelStarted, name: "duelStarted", direction: "serverToClient", frame: "binary", live: false },
  { type: MessageType.DuelEnded, name: "duelEnded", direction: "serverToClient", frame: "binary", live: false },
  { type: MessageType.PvpSwitch, name: "pvpSwitch", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Say, name: "say", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Whisper, name: "whisper", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Block, name: "block", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Unblock, name: "unblock", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Said, name: "said", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Whispered, name: "whispered", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Blocks, name: "blocks", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ChatRefused, name: "chatRefused", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Pose, name: "pose", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Posed, name: "posed", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.NameLookup, name: "nameLookup", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.NameMatches, name: "nameMatches", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ChannelSay, name: "channelSay", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.ChannelJoin, name: "channelJoin", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.ChannelLeave, name: "channelLeave", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.ChannelMute, name: "channelMute", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.ChannelSaid, name: "channelSaid", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Channels, name: "channels", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ChannelMuted, name: "channelMuted", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Afk, name: "afk", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Away, name: "away", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.WhisperAway, name: "whisperAway", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PartyInvite, name: "partyInvite", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PartyAnswer, name: "partyAnswer", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PartyLeave, name: "partyLeave", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PartyRemove, name: "partyRemove", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PartyPromote, name: "partyPromote", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PartySay, name: "partySay", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PartyInvited, name: "partyInvited", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Party, name: "party", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PartyNotice, name: "partyNotice", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PartySaid, name: "partySaid", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PartyRefused, name: "partyRefused", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PartyGrant, name: "partyGrant", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Emote, name: "emote", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Emotes, name: "emotes", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.EmojiShown, name: "emojiShown", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ActionShown, name: "actionShown", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.SpeakerBadge, name: "speakerBadge", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PartyInviteLookup, name: "partyInviteLookup", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PartyInviteMatches, name: "partyInviteMatches", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Bag, name: "bag", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.BagChanged, name: "bagChanged", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.UseItem, name: "useItem", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.BagMove, name: "bagMove", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.ItemRefused, name: "itemRefused", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Loot, name: "loot", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.LootTake, name: "lootTake", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.LootTaken, name: "lootTaken", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.BagSplit, name: "bagSplit", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.DestroyItem, name: "destroyItem", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Wallet, name: "wallet", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.BagSort, name: "bagSort", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.BagKeep, name: "bagKeep", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.BagKept, name: "bagKept", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Bar, name: "bar", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.BarSet, name: "barSet", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.LootGained, name: "lootGained", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PickUp, name: "pickUp", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.PickUpRefused, name: "pickUpRefused", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.DropItem, name: "dropItem", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.ToolBelt, name: "toolBelt", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ToolBeltSet, name: "toolBeltSet", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Snapshot, name: "snapshot", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Shrines, name: "shrines", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ThingEvent, name: "thingEvent", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Looks, name: "looks", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.MapPlaces, name: "mapPlaces", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.PartyPositions, name: "partyPositions", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.Explored, name: "explored", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.PartyVitals, name: "partyVitals", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.TravelOffer, name: "travelOffer", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.TravelState, name: "travelState", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Harvested, name: "harvested", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.HarvestEvent, name: "harvestEvent", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.EdgeView, name: "edgeView", direction: "serverToClient", frame: "binary", live: true },
  { type: MessageType.EdgeNear, name: "edgeNear", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Interact, name: "interact", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Interaction, name: "interaction", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.ReadingLayout, name: "readingLayout", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.TravelAsk, name: "travelAsk", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Travel, name: "travel", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.TravelCancel, name: "travelCancel", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.Dialogue, name: "dialogue", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.DialogueChoose, name: "dialogueChoose", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.DialogueClose, name: "dialogueClose", direction: "both", frame: "text", live: true },
  { type: MessageType.Harvest, name: "harvest", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.HarvestState, name: "harvestState", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.DoorRefused, name: "doorRefused", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.DoorPrompt, name: "doorPrompt", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.CompanionSet, name: "companionSet", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.CompanionName, name: "companionName", direction: "clientToServer", frame: "text", live: true },
  { type: MessageType.CompanionSay, name: "companionSay", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.CompanionNaming, name: "companionNaming", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.CompanionState, name: "companionState", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Sky, name: "sky", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.DoorBusy, name: "doorBusy", direction: "serverToClient", frame: "text", live: true },
  { type: MessageType.Source, name: "source", direction: "serverToClient", frame: "text", live: true },
];

/**
 * First id of the debug and admin range, rejected on production gateways.
 */
export const DEBUG_RANGE_START = 0xf000;

/**
 * Looks a message up by id.
 *
 * @param type Message id.
 * @returns The entry, or undefined for an unknown id.
 */
export function messageInfo(type: number): MessageInfo | undefined {
  return CATALOGUE.find((m) => m.type === type);
}

/**
 * Whether a message id may arrive from a direction in an environment: known and live, sent that way (or either way),
 * carried on that frame kind, and not a debug id on a production gateway.
 *
 * @param type Message id.
 * @param direction Who sent it.
 * @param frame Frame kind it arrived on.
 * @param production True on a production gateway.
 * @returns True when allowed.
 */
export function isAllowed(type: number, direction: MessageDirection, frame: FrameKind, production: boolean): boolean {
  if (production && type >= DEBUG_RANGE_START) {
    return false;
  }
  const info = messageInfo(type);
  return info !== undefined && (info.direction === direction || info.direction === "both") && info.frame === frame && info.live;
}

/**
 * Whether a client message is declared but not yet handled by this build: the catalogue names it, sent by the client
 * on this frame kind, and it is not live. Such a message is dropped and counted on the connection's not-ready window
 * before its layout or fields are read, so a page of a newer build meeting an older gateway during a deploy plays on
 * (ADR-074 decision 1). A server message, or one in the wrong direction or on the wrong frame, is never not ready.
 *
 * @param type Message id.
 * @param direction Who sent it.
 * @param frame Frame kind it arrived on.
 * @returns True when the message is declared for the client on this frame and not handled.
 */
export function isNotReady(type: number, direction: MessageDirection, frame: FrameKind): boolean {
  const info = messageInfo(type);
  return direction === "clientToServer" && info !== undefined && info.direction === direction && info.frame === frame && !info.live;
}
